import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TenantContextService } from '../tenant/tenant-context.service';
import { TenantLifecycleService } from '../tenant/tenant-lifecycle.service';
import { CONFIGURATION_NAV_FLAG_KEY, FeatureFlagService } from './feature-flag.service';

/**
 * Defense-in-depth guard for the `/cmi/config` page.
 *
 * The Configuration tab is an admin surface reserved for privileged users
 * (see the `app-layout.navigationItems` filter). Two preconditions must hold
 * for the route to activate:
 *
 * 1. The Amplitude `configuration-nav-enabled` flag is on for the caller
 *    (email allowlist / kill switch).
 * 2. The bootstrap user is privileged (`super_user` or `admin`).
 *
 * Tenant users — and any caller without the flag — are redirected to
 * `/home`. This must gate on the same signal as the nav item; a route
 * narrower than the nav would show an entry that bounces on click. The guard
 * awaits `TenantLifecycleService.bootstrapTenantContext()` before reading
 * `isPrivileged` so that a direct deep-link by a privileged user on a cold
 * load does not race past tenant resolution and get redirected spuriously.
 * The bootstrap stream is memoized via `shareReplay`, so this await never
 * issues a duplicate HTTP fetch alongside AppLayout's own subscription.
 */
export const configurationNavEnabledGuard: CanActivateFn = async () => {
  const flags = inject(FeatureFlagService);
  const router = inject(Router);
  const lifecycle = inject(TenantLifecycleService);
  const tenantContext = inject(TenantContextService);

  await flags.ready();
  if (!flags.isEnabled(CONFIGURATION_NAV_FLAG_KEY)) {
    return router.createUrlTree(['/home']);
  }

  try {
    await firstValueFrom(lifecycle.bootstrapTenantContext());
  } catch {
    // Bootstrap failed (no tenants, network error, etc.). The user has
    // nothing tenant-scoped to act on, so deny access conservatively.
    return router.createUrlTree(['/home']);
  }

  return tenantContext.isPrivileged() || router.createUrlTree(['/home']);
};
